/**
 * CollectionManager — a grid for reviewing and editing the whole catalog.
 * Each card shows the coral, its color/growth tags, and an "Edited" badge
 * when the operator has overridden it. Clicking a card opens the editable
 * detail pop-up (same one used everywhere). Edits persist to the Pi.
 */
import { ColorDots, GrowthPill } from './chips';
import { CoralThumb } from './CoralThumb';
import type { Coral } from './types';

export interface CollectionManagerProps {
  readonly corals: readonly Coral[];
  readonly editedIds: ReadonlySet<string>;
  readonly onSelectCoral: (coralId: string) => void;
}

export function CollectionManager({
  corals,
  editedIds,
  onSelectCoral,
}: CollectionManagerProps) {
  return (
    <div className="h-full overflow-y-auto pr-1">
      <p className="mb-3 text-xs text-text-3">
        {corals.length} coral{corals.length === 1 ? '' : 's'} ·{' '}
        {editedIds.size} edited · click any coral to edit its color, growth
        form, name, or notes (saved to your Pi).
      </p>
      {corals.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-3">
          No corals match the current filters.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {corals.map((coral) => (
            <button
              key={coral.id}
              type="button"
              onClick={() => onSelectCoral(coral.id)}
              className="flex items-center gap-3 rounded-xl bg-bg-elevated p-2.5 text-left ring-1 ring-surface-stroke transition-shadow hover:ring-accent/50"
            >
              <CoralThumb
                coral={coral}
                prefer="colony"
                className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="line-clamp-1 text-sm font-semibold text-text-1">
                    {coral.name}
                  </p>
                  {editedIds.has(coral.id) && (
                    <span className="rounded-full bg-accent-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase text-accent">
                      Edited
                    </span>
                  )}
                </div>
                <p className="line-clamp-1 text-xs italic text-text-3">
                  {coral.species}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <ColorDots colors={coral.colors} />
                  <GrowthPill form={coral.growthForm} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
